import { DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Form, InputNumber, Tooltip } from 'antd';
import { ToggleHeader, ToggleSwitch } from '@/components/toggle-switch';
import { cn } from '@/libs/utils';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';

/** Time tracking and timesheet review switches with their settings. */
export default function CompanyTimeSettings() {
  const {
    allowTimerKeys,
    attendanceModeKeys,
    autoSyncToPayrollKeys,
    defaultBillableKeys,
    form,
    isProfile,
    lockAfterApprovalKeys,
    maxHoursPerDayKeys,
    requireApprovalKeys,
    sectionClass,
    setTimeTrackingExpanded,
    setTimesheetReviewExpanded,
    t,
    timeTrackingChecked,
    timeTrackingExpanded,
    timeTrackingKeys,
    timeTrackingState,
    timesheetReviewEnabledChecked,
    timesheetReviewEnabledKeys,
    timesheetReviewEnabledState,
    timesheetReviewExpanded,
    timesheetReviewFlagNightKeys,
    timesheetReviewFlagOTKeys,
    timesheetReviewFlagPartialKeys,
    timesheetReviewFlagUnmarkedOtKeys,
    timesheetReviewMaxHoursKeys,
  } = useCompanyGeneralDataForm();
  // On the profile: laid out by CompanyTimeProfilePanel.
  if (isProfile) return null;

  return (
    <>
      {timeTrackingState?.visible && (
        <div className={sectionClass('vreme')}>
          {!isProfile && <hr className="my-5" />}

          <ToggleHeader
            title={t('timeTrackingPlatform', { ns: 'forms' })}
            className={cn(
              '',
              timeTrackingChecked && timeTrackingExpanded ? 'mb-5' : ''
            )}
          >
            <div className="flex items-center gap-3">
              {timeTrackingChecked && (
                <DownOutlined
                  className={cn(
                    'cursor-pointer text-xs text-gray-400 transition-transform',
                    !timeTrackingExpanded && '-rotate-90'
                  )}
                  onClick={() => setTimeTrackingExpanded((v) => !v)}
                />
              )}
              <ToggleSwitch
                name={timeTrackingKeys}
                form={form}
                disabled={timeTrackingState?.disabled}
                onChangeAction={(val) => val && setTimeTrackingExpanded(true)}
              />
            </div>
          </ToggleHeader>

          <div
            className={cn(
              timeTrackingChecked && timeTrackingExpanded
                ? 'block rounded-md border bg-white p-4'
                : 'hidden'
            )}
          >
            <ToggleHeader
              title={t('requireApproval', { ns: 'forms' })}
              className="mb-5"
            >
              <ToggleSwitch name={requireApprovalKeys} form={form} />
            </ToggleHeader>

            <ToggleHeader
              title={t('autoSyncToPayroll', { ns: 'forms' })}
              className="mb-5"
            >
              <ToggleSwitch name={autoSyncToPayrollKeys} form={form} />
            </ToggleHeader>

            <ToggleHeader
              title={t('defaultBillable', { ns: 'forms' })}
              className="mb-5"
            >
              <ToggleSwitch name={defaultBillableKeys} form={form} />
            </ToggleHeader>

            <ToggleHeader
              title={t('allowTimer', { ns: 'forms' })}
              className="mb-5"
            >
              <ToggleSwitch name={allowTimerKeys} form={form} />
            </ToggleHeader>

            <ToggleHeader
              title={t('lockAfterApproval', { ns: 'forms' })}
              className="mb-5"
            >
              <ToggleSwitch name={lockAfterApprovalKeys} form={form} />
            </ToggleHeader>

            <ToggleHeader
              className="mb-5"
              title={
                <>
                  {t('attendanceMode', { ns: 'forms' })}
                  <Tooltip
                    placement="topLeft"
                    title={t('attendanceModeTooltip', { ns: 'forms' })}
                  >
                    <InfoCircleOutlined className="ml-2 text-base text-zinc-400" />
                  </Tooltip>
                </>
              }
            >
              <ToggleSwitch name={attendanceModeKeys} form={form} />
            </ToggleHeader>

            <Form.Item
              name={maxHoursPerDayKeys}
              label={t('maxHoursPerDay', { ns: 'forms' })}
              className="mb-0"
            >
              <InputNumber min={1} max={24} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </div>
      )}

      {timesheetReviewEnabledState?.visible && (
        <div className={sectionClass('vreme')}>
          {!isProfile && <hr className="my-5" />}

          <ToggleHeader
            title={t('timesheetSettings', { ns: 'forms' })}
            className={cn(
              '',
              timesheetReviewEnabledChecked && timesheetReviewExpanded
                ? 'mb-5'
                : ''
            )}
          >
            <div className="flex items-center gap-3">
              {timesheetReviewEnabledChecked && (
                <DownOutlined
                  className={cn(
                    'cursor-pointer text-xs text-gray-400 transition-transform',
                    !timesheetReviewExpanded && '-rotate-90'
                  )}
                  onClick={() => setTimesheetReviewExpanded((v) => !v)}
                />
              )}
              <ToggleSwitch
                name={timesheetReviewEnabledKeys}
                form={form}
                disabled={timesheetReviewEnabledState?.disabled}
                onChangeAction={(val) =>
                  val && setTimesheetReviewExpanded(true)
                }
              />
            </div>
          </ToggleHeader>

          <div
            className={cn(
              timesheetReviewEnabledChecked && timesheetReviewExpanded
                ? 'block rounded-md border bg-white p-4'
                : 'hidden'
            )}
          >
            <p className="mb-4 text-xs text-gray-500">
              {t('timesheetReviewDescription', { ns: 'forms' })}
            </p>

            <Form.Item
              name={timesheetReviewMaxHoursKeys}
              label={t('maxHoursPerDay', { ns: 'forms' })}
              className="mb-5"
            >
              <InputNumber min={1} max={24} style={{ width: '100%' }} />
            </Form.Item>

            <ToggleHeader
              title={t('flagOTSameDayAsPublicHoliday', { ns: 'forms' })}
              className="mb-5"
            >
              <ToggleSwitch name={timesheetReviewFlagOTKeys} form={form} />
            </ToggleHeader>

            <ToggleHeader
              title={t('flagNightWithRegularEqFond', { ns: 'forms' })}
              className="mb-5"
            >
              <ToggleSwitch name={timesheetReviewFlagNightKeys} form={form} />
            </ToggleHeader>

            <ToggleHeader
              title={t('flagPartialPublicHoliday', { ns: 'forms' })}
              className="mb-5"
            >
              <ToggleSwitch name={timesheetReviewFlagPartialKeys} form={form} />
            </ToggleHeader>

            <ToggleHeader
              title={t('flagUnmarkedOvertime', { ns: 'forms' })}
              className="mb-0"
            >
              <ToggleSwitch
                name={timesheetReviewFlagUnmarkedOtKeys}
                form={form}
              />
            </ToggleHeader>
          </div>
        </div>
      )}
    </>
  );
}
